import {ErrorBoundary} from '@sentry/react';
import {
  dispatchRenderCrashEvent,
  isSentryReporting,
  type RenderCrashDetail,
} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {ReactNode} from 'react';

import {ErrorPage} from '@dms/shared';

type SentryErrorBoundaryProps = {
  children?: ReactNode;
};

const UNKNOWN_CRASH_NAME = 'Error';
const UNKNOWN_CRASH_MESSAGE = 'Unknown render error';

const toRenderCrashDetail = (
  error: unknown,
  componentStack: string,
  sentryEventId: string
): RenderCrashDetail => {
  // Only when Sentry is really reporting: it hands back an id even when it drops the event, and a
  // report must not link to an event that was never delivered.
  const crashContext = {
    componentStack,
    sentryEventId: isSentryReporting() ? sentryEventId : undefined,
  };

  return match(error)
    .with(Pattern.instanceOf(Error), (thrownError) => ({
      name: thrownError.name,
      message: thrownError.message,
      stack: thrownError.stack,
      ...crashContext,
    }))
    .with(Pattern.string, (message) => ({name: UNKNOWN_CRASH_NAME, message, ...crashContext}))
    .otherwise(() => ({
      name: UNKNOWN_CRASH_NAME,
      message: UNKNOWN_CRASH_MESSAGE,
      ...crashContext,
    }));
};

export function SentryErrorBoundary(props: SentryErrorBoundaryProps) {
  return (
    <ErrorBoundary
      fallback={ErrorPage}
      onError={(error, componentStack, eventId) =>
        dispatchRenderCrashEvent(toRenderCrashDetail(error, componentStack, eventId))
      }
    >
      {props.children}
    </ErrorBoundary>
  );
}
